import React from 'react';
import styled from 'styled-components';
import { COLOR_SYSTEM, Flex, Text, Button, Icon, Static } from 'components/atoms';
import Tabs from 'components/molecules/Tabs';
import { ConfirmManager } from 'components/organisms/CommonUI';
import { formatGenderShort } from 'utils/gender';
// 날짜 포맷 함수
const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const Container = styled.div`
  width: 100%;
  min-height: calc(100vh - 80px);
  padding: 40px 60px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
`;

const ContentArea = styled.div`
  margin-top: 24px;
  background-color: white;
  border-radius: 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  padding: 24px;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${COLOR_SYSTEM.get("Gray")?.[200]};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
`;

const PageButton = styled.button<{ isActive?: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${({ isActive }) => isActive ? COLOR_SYSTEM.get("Blue")?.[400] : COLOR_SYSTEM.get("Gray")?.[200]};
  background-color: ${({ isActive }) => isActive ? COLOR_SYSTEM.get("Blue")?.[400] : 'white'};
  color: ${({ isActive }) => isActive ? 'white' : COLOR_SYSTEM.get("Gray")?.[700]};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${COLOR_SYSTEM.get("Blue")?.[400]};
    color: ${COLOR_SYSTEM.get("Blue")?.[400]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;


interface TrashTemplateProps {
  activeTab: 'patients' | 'aiReviews';
  onTabChange: (tab: 'patients' | 'aiReviews') => void;
  patients: any[];
  aiReviews: any[];
  isLoading: boolean;
  isError: boolean;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onRestorePatient: (patientId: number) => void;
  onRestoreAiReview: (reviewId: number) => void;
  isRestoring: boolean;
}

const TrashTemplate: React.FC<TrashTemplateProps> = ({
  activeTab,
  onTabChange,
  patients,
  aiReviews,
  isLoading,
  isError,
  currentPage,
  pageSize,
  onPageChange,
  onRestorePatient,
  onRestoreAiReview,
  isRestoring,
}) => {
  const tabs = [
    { id: 'patients', label: '환자' },
    { id: 'aiReviews', label: '검토보고서' },
  ];

  const handleRestoreClick = (type: 'patient' | 'aiReview', id: number, name?: string) => {
    const itemType = type === 'patient' ? '환자' : '검토보고서';
    const itemName = name ? ` (${name})` : '';
    
    ConfirmManager.show({
      title: '복원 확인',
      body: `정말로 이 ${itemType}${itemName}를 복원하시겠습니까?`,
      buttons: [
        <Button
          key="cancel"
          type="Text"
          size="Medium"
          variant="Outlined"
          color="Secondary"
          onClick={() => ConfirmManager.hide()}
        >
          취소
        </Button>,
        <Button
          key="confirm"
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          onClick={() => {
            if (type === 'patient') {
              onRestorePatient(id);
            } else {
              onRestoreAiReview(id);
            }
            ConfirmManager.hide();
          }}
          disabled={isRestoring}
        >
          복원
        </Button>,
      ],
    });
  };

  const renderPatientItem = (patient: any) => {
    const patientLabel = patient.memo 
      ? `환자 #${patient.id} - ${patient.memo}` 
      : `환자 #${patient.id}`;
    
    const genderText = formatGenderShort(patient.gender, '성별 미지정');
    const birthDateText = patient.birthDate ? formatDate(patient.birthDate) : '생년월일 미지정';
    
    return (
      <ListItem key={patient.id}>
        <InfoSection>
          <Flex gap="8px" alignItems="center">
            <Text textType="Body1Bold" color={COLOR_SYSTEM.get("Gray")?.[900]}>
              {patientLabel}
            </Text>
          </Flex>
          <Flex gap="12px" alignItems="center">
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
              {genderText}, {birthDateText}
            </Text>
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
              삭제일: {formatDate(patient.deletedAt)}
            </Text>
          </Flex>
        </InfoSection>
        <Button
          size="Small"
          type="Text"
          variant="Outlined"
          color="Primary"
          onClick={() => handleRestoreClick('patient', patient.id, patientLabel)}
          disabled={isRestoring}
        >
          복원
        </Button>
      </ListItem>
    );
  };

  const renderAiReviewItem = (review: any) => {
    const reviewLabel = `AI 검토보고서 #${review.id}`;
    const statusText = review.status === 400 ? '완료' : 
                      review.status === 200 ? '처리중' : 
                      review.status === -1 ? '오류' : '알 수 없음';
    
    return (
      <ListItem key={review.id}>
        <InfoSection>
          <Flex gap="8px" alignItems="center">
            <Text textType="Body1Bold" color={COLOR_SYSTEM.get("Gray")?.[900]}>
              {reviewLabel}
            </Text>
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
              (환자 #{review.patientId})
            </Text>
          </Flex>
          <Flex gap="12px" alignItems="center">
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
              상태: {statusText}
            </Text>
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
              검토일: {formatDate(review.reviewDate)}
            </Text>
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
              삭제일: {formatDate(review.deletedAt)}
            </Text>
          </Flex>
        </InfoSection>
        <Button
          size="Small"
          type="Text"
          variant="Outlined"
          color="Primary"
          onClick={() => handleRestoreClick('aiReview', review.id, reviewLabel)}
          disabled={isRestoring}
        >
          복원
        </Button>
      </ListItem>
    );
  };

  const currentData = activeTab === 'patients' ? patients : aiReviews;
  // 서버 사이드 페이지네이션이므로 전체 페이지 수를 알 수 없음
  // 현재 페이지에 데이터가 pageSize만큼 있으면 다음 페이지가 있다고 가정
  const hasNextPage = currentData && currentData.length === pageSize;
  const hasData = currentData && currentData.length > 0;

  const renderPagination = () => {
    // 첫 페이지이고 데이터가 없을 때만 페이지네이션 숨김
    if (currentPage === 1 && !hasData) return null;

    return (
      <PaginationContainer>
        <PageButton
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <Icon name="Chevron Left" size={16} />
        </PageButton>
        
        <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]} style={{ margin: '0 16px' }}>
          페이지 {currentPage}
        </Text>
        
        <PageButton
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNextPage}
        >
          <Icon name="Chevron Right" size={16} />
        </PageButton>
      </PaginationContainer>
    );
  };

  return (
    <Container>
      <Flex alignItems="center" customStyle={{ marginBottom: "20px" }}>
        <Icon name="Trash Filled" size={24} color={COLOR_SYSTEM.get("Gray")?.[900]} />
        <Text
          textType="H1"
          color={COLOR_SYSTEM.get("Gray")?.[900]}
          style={{ marginLeft: "12px" }}
        >
          휴지통
        </Text>
      </Flex>

      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={(tab) => onTabChange(tab as any)} />

      <ContentArea>
        {isLoading ? (
          <LoadingState>
            <Icon name="Spinner" size={32} color={COLOR_SYSTEM.get("Blue")?.[400]} />
          </LoadingState>
        ) : isError ? (
          <EmptyState>
            <Icon name="Exclamation Mark Circle Filled" size={48} color={COLOR_SYSTEM.get("Red")?.[400]} />
            <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")?.[700]} style={{ marginTop: "16px" }}>
              데이터를 불러오는 중 오류가 발생했습니다.
            </Text>
          </EmptyState>
        ) : !hasData ? (
          <EmptyState>
            <Icon name="Trash" size={48} color={COLOR_SYSTEM.get("Gray")?.[300]} />
            <Text textType="Body1Medium" color={COLOR_SYSTEM.get("Gray")?.[700]} style={{ marginTop: "16px" }}>
              삭제된 {activeTab === 'patients' ? '환자' : '검토보고서'}가 없습니다.
            </Text>
          </EmptyState>
        ) : (
          <>
            <ListContainer>
              {activeTab === 'patients'
                ? patients.map(renderPatientItem)
                : aiReviews.map(renderAiReviewItem)}
            </ListContainer>
          </>
        )}
        {/* 페이지네이션은 에러가 아닌 경우 항상 표시 */}
        {!isLoading && !isError && renderPagination()}
      </ContentArea>
    </Container>
  );
};

export default TrashTemplate;